import {Button as MantineButton, Group, Menu, Modal, Stack, Text, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/analytics-products/ModelHubAnalyticsConstants';
import FolderSelector from 'file-browser/FolderSelector';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as TaskRunner from 'util/TaskRunner';

interface ExportBatchToDatasetButtonProps {
  actionVariant?: 'button' | 'menuItem';
  batchId: string;
  batchName: string;
  batchFolderId: string | null;
}

interface ExportResponse {
  datasetId: string;
  webUrl: string;
}

const DATASET_NAME_SUFFIX = ' properties';
const MAX_DATASET_NAME_LENGTH = 255;

const getDefaultDatasetName = (batchName: string): string =>
  `${batchName.slice(0, MAX_DATASET_NAME_LENGTH - DATASET_NAME_SUFFIX.length)}${DATASET_NAME_SUFFIX}`;

/**
 * Per-row action to export a property-prediction batch's results to a Data Catalog Dataset.
 * Rendered only for succeeded property-prediction batches (the caller gates on category + status).
 *
 * Opens a modal to confirm the dataset name and folder (defaulting to the batch's name and folder),
 * then queues creation and navigates to the Dataset once the task completes.
 */
export default function ExportBatchToDatasetButton({
  actionVariant = 'button',
  batchId,
  batchName,
  batchFolderId,
}: ExportBatchToDatasetButtonProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [name, setName] = useState(getDefaultDatasetName(batchName));
  const [folderId, setFolderId] = useState<string | null>(batchFolderId);

  const openModal = (): void => {
    // Reset to defaults each time the modal opens so a prior edit doesn't leak into the next export.
    setName(getDefaultDatasetName(batchName));
    setFolderId(batchFolderId);
    setIsOpen(true);
  };

  const runExport = (): void => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.BATCHES_EXPORT_TO_DATASET_CLICKED);
    setIsExporting(true);
    TaskRunner.postJSONPromise({
      url: '/1/api/model-hub/prediction-batch:export-to-dataset',
      data: {predictionBatchIds: [batchId], datasetName: name, folderId},
    })
      .then((resp: ExportResponse) => {
        setIsOpen(false);
        navigateToURL(resp.webUrl);
      })
      .catch((error: {userMessage?: string; responseJSON?: {error?: {userMessage?: string}}}) => {
        notifications.error(
          error.userMessage ?? error.responseJSON?.error?.userMessage ?? 'Failed to export to dataset.'
        );
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  const canSubmit = name.trim().length > 0 && folderId != null;

  return (
    <>
      {actionVariant === 'menuItem' ? (
        <Menu.Item icon={<BenchlingIcon icon="file-chart-column" />} onClick={openModal}>
          Export to dataset
        </Menu.Item>
      ) : (
        <Button
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          text="Export to dataset"
          onClick={openModal}
        />
      )}
      <Modal
        opened={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title="Export to dataset"
        centered={true}
      >
        <Stack spacing="md">
          <TextInput
            label="Dataset name"
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
            }}
            required={true}
            maxLength={255}
          />
          <div>
            <Text size="sm" fw={500} mb={4}>
              Folder
            </Text>
            <FolderSelector
              value={folderId}
              onChange={(e: {target: {value: string | null}}) => {
                setFolderId(e.target.value);
              }}
              allowNone={false}
              enableRedesign={false}
              error=""
              level="append"
              noFolderText="Choose folder"
              readOnly={false}
              requireSubfolderCreatableAuth={false}
              showInventory={false}
              showNotebook={true}
              showRegistries={false}
              unsetOnFetchFail={false}
              checkExistingItemForDataModsMovement={false}
            />
          </div>
          <Group position="right" spacing="sm">
            <MantineButton
              variant="default"
              onClick={() => {
                setIsOpen(false);
              }}
              disabled={isExporting}
            >
              Cancel
            </MantineButton>
            <MantineButton
              variant="filled"
              onClick={() => {
                runExport();
              }}
              loading={isExporting}
              disabled={!canSubmit}
            >
              Create dataset
            </MantineButton>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
